/**
 * Format currency amount with locale-aware formatting
 * 
 * @param {number} amount - The amount to format (in euros, not cents)
 * @param {string} locale - Optional locale code (e.g., 'en', 'fr', 'de'). If not provided, uses 'en-US'
 * @param {object} options - Optional formatting options
 * @param {number} options.minimumFractionDigits - Minimum fraction digits (default: 2)
 * @param {number} options.maximumFractionDigits - Maximum fraction digits (default: 2)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, locale = 'en', options = {}) {
  // Handle null, undefined, or empty values
  if (amount === null || amount === undefined || amount === '') {
    return '-';
  }
  
  // Handle zero explicitly
  if (amount === 0) {
    amount = 0;
  }
  
  // Convert to number if it's a string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Check if conversion resulted in NaN
  if (isNaN(numAmount)) {
    return '-';
  }
  
  // Map locale codes to Intl locale strings
  const localeMap = {
    'en': 'en-US',
    'fr': 'fr-FR',
    'de': 'de-DE',
    'lb': 'de-DE', // Luxembourgish uses German formatting
    'it': 'it-IT',
    'es': 'es-ES',
    'pt': 'pt-PT'
  };
  
  const currencyLocale = localeMap[locale] || 'en-US';
  
  // Default options
  const defaultOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options
  };
  
  return new Intl.NumberFormat(currencyLocale, {
    style: 'currency',
    currency: 'EUR',
    ...defaultOptions
  }).format(numAmount);
}

/**
 * Format currency from cents (divides by 100 first)
 * This is a convenience function for when amounts are stored in cents
 * 
 * @param {number} amount - The amount in cents
 * @param {string} locale - Optional locale code
 * @param {object} options - Optional formatting options
 * @returns {string} Formatted currency string
 */
export function formatCurrencyAmount(amount, locale = 'en', options = {}) {
  if (amount === null || amount === undefined || amount === '') {
    return '-';
  }
  
  const euros = typeof amount === 'number' ? amount.toFixed(2) : parseFloat(amount).toFixed(2);
  
  if (isNaN(euros)) {
    return '-';
  }
  
  return formatCurrency(euros, locale, options);
}

/**
 * Keep whole euros only. Drops cents typed with `,` or `.` without clearing the integer part.
 * @param {string|number|null|undefined} raw
 * @returns {number|''}
 */
export function sanitizeWholeEuroInput(raw) {
  const beforeSeparator = String(raw ?? '').split(/[.,]/)[0];
  const digits = beforeSeparator.replace(/\D/g, '');
  if (digits === '') return '';
  const num = parseInt(digits, 10);
  return Number.isFinite(num) ? num : '';
}

